import { useCallback, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useStepUpRefusal } from "@/shared/hooks/useStepUpRefusal";
import useTranslations from "@/shared/lang/useTranslations";
import { hasStepUpMethod, STEP_UP_EMAIL_REQUEST } from "@/shared/utils/stepUp";
import { isTwoFactorCodeError, stepUpRefusal } from "@/shared/utils/twoFactorErrors";

export type TwoFactorAction<T> = (code: string) => Promise<T>;

export interface TwoFactorChallengeController {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (code: string) => Promise<void>;
}

export interface TwoFactorRequiredController {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The person created a security PIN in the notice: go straight on to the challenge of the pending action. */
  onEnrolled: () => void;
}

export interface UseTwoFactorActionResult<T> {
  /**
   * Runs `action` behind the step-up prompt. Resolves with the action's result once a code was accepted, or
   * with `null` when the person cancelled the challenge, or closed the enrolment notice without setting up a
   * method. A member who creates a security PIN in that notice goes straight on to the challenge, and the
   * same action runs with the new PIN. Every failure that is not a refused code (a 409, a network error, a
   * validation error) closes the challenge and REJECTS, so the caller handles it where the action lives.
   */
  start: (action: TwoFactorAction<T>) => Promise<T | null>;
  challenge: TwoFactorChallengeController;
  required: TwoFactorRequiredController;
  /** True while the guarded request is in flight. */
  isRunning: boolean;
  isEnrolled: boolean;
}

interface Pending<T> {
  action: TwoFactorAction<T>;
  resolve: (value: T | null) => void;
  reject: (error: unknown) => void;
}

export interface UseTwoFactorActionOptions {
  /**
   * Close the challenge as soon as the code is sent, and open it again with the error if the code is refused.
   * For requests that keep working long after the code is checked: a prompt left on "Verifying…" for that
   * long reads as broken. The caller shows its own progress while `isRunning`.
   */
  closeOnSend?: boolean;
}

/**
 * The one flow every `x-two-factor-code` request shares: pre-check that the user has a step-up method (an
 * unenrolled user would get a 403 and be redirected away mid-flow), open the challenge, run the request with
 * the typed code or PIN, keep the modal open on a refused one, and hand every other outcome back to the
 * caller. Render the two dialogs it drives with `TwoFactorActionModals`.
 */
export function useTwoFactorAction<T = unknown>({
  closeOnSend = false,
}: UseTwoFactorActionOptions = {}): UseTwoFactorActionResult<T> {
  const { user } = useAuth();
  const { t } = useTranslations("global");
  const refusalMessage = useStepUpRefusal();
  const isEnrolled = hasStepUpMethod(user);

  const [challengeOpen, setChallengeOpen] = useState(false);
  const [requiredOpen, setRequiredOpen] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const pendingRef = useRef<Pending<T> | null>(null);
  // Read synchronously by the close handler: the state flips a render later than the request starts.
  const runningRef = useRef(false);

  const settle = useCallback((value: T | null) => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    pending?.resolve(value);
  }, []);

  const start = useCallback(
    (action: TwoFactorAction<T>) =>
      new Promise<T | null>((resolve, reject) => {
        pendingRef.current?.resolve(null);
        pendingRef.current = { action, resolve, reject };
        // Without a method the action waits behind the enrolment notice: creating a PIN there carries it on.
        if (!isEnrolled) setRequiredOpen(true);
        else setChallengeOpen(true);
      }),
    [isEnrolled],
  );

  const onChallengeOpenChange = useCallback(
    (open: boolean) => {
      // Once the code is sent the request decides, not the dialog: closing now would answer "cancelled"
      // while the request goes on and may succeed, and its result would land on a promise already settled.
      if (!open && runningRef.current) return;
      setChallengeOpen(open);
      if (!open) settle(null);
    },
    [settle],
  );

  const onChallengeSubmit = useCallback(
    async (code: string) => {
      const pending = pendingRef.current;
      if (!pending) return;
      // Asking for an email code only gets "sent" back, so the challenge stays up for the code to be typed.
      const hidesChallenge = closeOnSend && code !== STEP_UP_EMAIL_REQUEST;
      runningRef.current = true;
      setIsRunning(true);
      // Closed without settling: the request decides, as when the dialog refuses to close mid-request.
      if (hidesChallenge) setChallengeOpen(false);
      try {
        const result = await pending.action(code);
        pendingRef.current = null;
        pending.resolve(result);
      } catch (error: unknown) {
        if (isTwoFactorCodeError(error)) {
          // The modal is still awaiting this submit, so the error thrown below lands in it once it reopens.
          if (hidesChallenge) setChallengeOpen(true);
          throw stepUpRefusal(error, refusalMessage(error, t("two_factor_challenge_error_invalid_code")));
        }
        pendingRef.current = null;
        setChallengeOpen(false);
        pending.reject(error);
      } finally {
        runningRef.current = false;
        setIsRunning(false);
      }
    },
    [t, closeOnSend, refusalMessage],
  );

  const onRequiredOpenChange = useCallback(
    (open: boolean) => {
      setRequiredOpen(open);
      if (!open) settle(null);
    },
    [settle],
  );

  const onEnrolled = useCallback(() => {
    setRequiredOpen(false);
    if (pendingRef.current) setChallengeOpen(true);
  }, []);

  return {
    start,
    challenge: { open: challengeOpen, onOpenChange: onChallengeOpenChange, onSubmit: onChallengeSubmit },
    required: { open: requiredOpen, onOpenChange: onRequiredOpenChange, onEnrolled },
    isRunning,
    isEnrolled,
  };
}
